import React from "react"
import Slider from "@kit/design/slider"
import Grid from "@kit/design/grid"
import Stack from "@kit/design/stack"
import { Alignment } from "core/alignment"
import { ColorInput } from "core/color-picker"
import { Field } from "core/form"
import {
  GroupWrapper,
  RadioButtonGroup
} from "app/shared/components/radio-button-group"
import { LinkTriggerForm } from "app/editor/plugins/link-triggers/form"
import { analytics } from "utils/analytics"
import { useTrailingDebounce as useDebounce } from "hooks/use-trailing-debounce"

const SIZE_OPTIONS = [
  { text: "Small", value: "small" },
  { text: "Medium", value: "medium" },
  { text: "Large", value: "large" }
]

const ROUNDED_CORNER_OPTIONS = [
  { text: "None", value: "none" },
  { text: "Small", value: "small" },
  { text: "Large", value: "large" },
  { text: "Full", value: "full" }
]

const WIDTH_OPTIONS = [
  { text: "Auto", value: "auto" },
  { text: "Full", value: "100%" }
]

export const ButtonSetting = ({ editor, node }) => {
  const [trackChange] = useDebounce(({ name, value }) => {
    analytics.event("block_change", {
      block_type: "button",
      property: name,
      value
    })
  }, 1000)

  const handleChange = event => {
    const { name, value } = event.target

    editor.command("setNodeByKey", node.key, {
      data: { ...node.data.toJS(), [name]: value }
    })

    trackChange({ name, value })
  }

  const handleRadioClick = (name, value) => {
    handleChange({ target: { name, value } })
  }

  const {
    size,
    alignment,
    color,
    backgroundColor,
    margin,
    width,
    roundedCornerOption
  } = node.data.toJS()

  return (
    <Stack spacing={4}>
      <LinkTriggerForm editor={editor} node={node} />
      <Grid columns="repeat(2, 1fr)" gap=".5rem">
        <Field
          control={ColorInput}
          label="Background color"
          name="backgroundColor"
          onChange={handleChange}
          value={backgroundColor}
        />
        <Field
          control={ColorInput}
          label="Text color"
          name="color"
          onChange={handleChange}
          value={color}
        />
      </Grid>
      <GroupWrapper label="Size" className="text-xs text-gray-800">
        <RadioButtonGroup
          keyName="size"
          currentValue={size}
          update={handleRadioClick}
          options={SIZE_OPTIONS}
          buttonGroupClasses="w-full flex space-between justify-between bg-gray-100 border border-gray-200 rounded-sm text-gray-800"
          buttonClasses="text-gray-700 hover:text-gray-800 focus:text-gray-800 text-xs"
          activeButtonClasses="bg-blue-500 text-white text-xs"
          spacing={0}
        />
      </GroupWrapper>
      <GroupWrapper label="Rounded corners" className="text-xs text-gray-800">
        <RadioButtonGroup
          keyName="roundedCornerOption"
          currentValue={roundedCornerOption}
          update={handleRadioClick}
          options={ROUNDED_CORNER_OPTIONS}
          buttonGroupClasses="w-full flex space-between justify-between bg-gray-100 border border-gray-200 rounded-sm text-gray-800"
          buttonClasses="text-gray-700 hover:text-gray-800 focus:text-gray-800 text-xs"
          activeButtonClasses="bg-blue-500 text-white text-xs"
          spacing={0}
        />
      </GroupWrapper>
      <Grid columns="repeat(2, 1fr)" gap=".5rem">
        <GroupWrapper label="Width" className="text-xs text-gray-800">
          <RadioButtonGroup
            name="width"
            keyName="width"
            currentValue={width}
            update={handleRadioClick}
            options={WIDTH_OPTIONS}
            buttonGroupClasses="w-full flex space-between justify-between bg-gray-100 border border-gray-200 rounded-sm text-gray-800 mt-1"
            buttonClasses="text-gray-700 hover:text-gray-800 focus:text-gray-800 text-xs"
            activeButtonClasses="bg-blue-500 text-white text-xs"
            spacing={0}
          />
        </GroupWrapper>
        <Field
          control={Slider}
          label="Margin"
          name="margin"
          min={0}
          max={40}
          value={margin}
          onChange={handleChange}
          controlClassName="border border-gray-200 rounded-sm"
        />
      </Grid>
      <div>
        <div className="inline-block text-xs font-semibold leading-snug text-gray-800 mb-1">
          Alignment
        </div>
        <Alignment
          alignment={alignment}
          onChange={({ name, value }) =>
            handleChange({ target: { name, value } })
          }
        />
      </div>
    </Stack>
  )
}
